import { Suspense, useState, useEffect, useMemo } from "react";
import { Route, Routes, useLocation, Navigate } from "react-router-dom";
import { prefixRoutes } from "@/helpers/prefixRoutes";
import { CookieConsentProvider } from "./components/CookieConsent";
import AppRoutes from "@/AppRoutes";
import Layout from "@/components/Layout";
import LayoutNoHeader from "@/components/LayoutNoHeader";
import AdminLayout from "@/admin/AdminLayout";
import ManagerLayout from "@/manager/ManagerLayout";
import { useGlobalState } from "@/helpers/useGlobalState";
import { setLanguage } from "@/helpers/application";
import { getGlobalContent, getGlobalContentitem } from "@/helpers/contentHelpers";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { loginRequest } from "@/auth/msalConfig";
import { setPostLoginHash } from "@/helpers/setPostLoginHash";
import "@/css/app.css";

export default function App() {
    const [loading, setLoading] = useState(true);
    const { globalState, updateGlobalState } = useGlobalState();
    const location = useLocation();

    const { instance, inProgress } = useMsal();
    const isAuthenticated = useIsAuthenticated();

    // --- URL parsing (HashRouter + BrowserRouter proof) ---
    // --- URL parsing (werkt voor HashRouter én voor /?lng=... start) ---

    const rawHash = window.location.hash ?? ""; // "#/en/academy?lng=en-GB" of ""
    const hasHashRoute = rawHash.startsWith("#/");

    const hashWithoutSharp = hasHashRoute ? rawHash.slice(1) : ""; // "/en/academy?lng=en-GB" of ""
    const [hashPath = "", hashQuery = ""] = hashWithoutSharp.split("?");

    // Als er een hash-route is, is dat de route. Anders is de route gewoon pathname.
    const effectivePath = hasHashRoute ? (hashPath || "/") : (location.pathname || "/");

    // Query komt uit hash als die er is, anders uit de normale search (?lng=...)
    const queryString = hasHashRoute
        ? hashQuery
        : (window.location.search ?? "").replace(/^\?/, "");

    const params = new URLSearchParams(queryString);
    const lngParam = params.get("lng");

    // EN detectie op basis van effectivePath
    const isEnPath = effectivePath.startsWith("/en");

    // Academy host: productie subdomain OR localhost (development)
    const isAcademyHost = 
        window.location.hostname === "academy.malencoprotection.nl" ||
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1";

    // Publiek op academy subdomein = alles BEHALVE admin en programs routes
    const isPublicAcademyPath = 
        !effectivePath.startsWith("/admin") && 
        !effectivePath.startsWith("/programs") &&
        effectivePath !== "/me";

    // Auth needed: academy host AND not public path
    const needsAuth = isAcademyHost && !isPublicAcademyPath;

    // EN routes = prefixed copy of all routes with a path
    const enRoutes = useMemo(() => prefixRoutes(AppRoutes as any, "/en"), []);

    // Pak je index(Home) element uit AppRoutes zodat we hem niet opnieuw hoeven importeren
    const homeElement = (AppRoutes as any).find((r: any) => r.index)?.element ?? null;

    // Recursieve renderer met layout-inheritance
    function renderRoutesRecursively(
        routes: readonly any[],
        parentLayout: "default" | "noheader" | "admin" | "manager" | undefined = undefined
    ) {
        const LAYOUTS = {
            noheader: LayoutNoHeader,
            default: Layout,
            admin: AdminLayout,
            manager: ManagerLayout,
        } as const;

        return routes.map((r: any, i: number) => {
            const thisLayout = (r.layout as keyof typeof LAYOUTS) ?? parentLayout ?? "default";
            const Wrapper = LAYOUTS[thisLayout] ?? Layout;

            const hasChildren = Array.isArray(r.children) && r.children.length > 0;

            if (hasChildren) {
                return (
                    <Route
                        key={`node-${r.path ?? `index-${i}`}`}
                        path={r.path}
                        element={<Wrapper titleCode={r.titleCode} />}
                    >
                        {r.index && r.element && <Route index element={r.element} />}
                        {!r.index && r.element && <Route index element={r.element} />}

                        {r.children.map((child: any, j: number) => {
                            const childHasChildren = Array.isArray(child.children) && child.children.length > 0;
                            const childLayout = (child.layout as keyof typeof LAYOUTS) ?? thisLayout;

                            if (childHasChildren) {
                                return renderRoutesRecursively([child], thisLayout)[0];
                            }

                            if (childLayout === thisLayout) {
                                return child.index ? (
                                    <Route key={`leaf-${j}-index`} index element={child.element} />
                                ) : (
                                    <Route
                                        key={`leaf-${j}-${child.path ?? "index"}`}
                                        path={child.path}
                                        element={child.element}
                                    />
                                );
                            } else {
                                const ChildWrapper = LAYOUTS[childLayout] ?? Layout;
                                return (
                                    <Route
                                        key={`leaf-${j}-${child.path ?? "index"}`}
                                        path={child.path}
                                        element={<ChildWrapper titleCode={child.titleCode} />}
                                    >
                                        <Route index element={child.element} />
                                    </Route>
                                );
                            }
                        })}
                    </Route>
                );
            }

            return (
                <Route
                    key={`leaf-${r.path ?? `index-${i}`}`}
                    path={r.path}
                    element={<Wrapper titleCode={r.titleCode} />}
                >
                    <Route index element={r.element} />
                </Route>
            );
        });
    }

    // Hash restore is handled in main.tsx during bootstrap

    useEffect(() => {
        if (!needsAuth) return;

        // CRITICAL: Wait until MSAL has finished checking auth status
        if (inProgress !== InteractionStatus.None) {
            return;
        }

        if (!isAuthenticated) {
            setPostLoginHash();
            instance.loginRedirect(loginRequest);
        }
    }, [needsAuth, isAuthenticated, inProgress, instance]);

    useEffect(() => {
        (async () => {
            try {
                let desired: "nl-NL" | "en-GB" = isEnPath ? "en-GB" : "nl-NL";

                // ?lng override
                if (lngParam === "en-GB" || lngParam === "nl-NL") {
                    desired = lngParam;

                    const shouldBeEn = desired === "en-GB";
                    const isCurrentlyEn = isEnPath;

                    if (shouldBeEn !== isCurrentlyEn) {
                        // verwijder lng, behoud andere params (utm etc.)
                        params.delete("lng");
                        const rest = params.toString();
                        const qs = rest ? `?${rest}` : "";

                        // base path zonder /en
                        const basePath = effectivePath.replace(/^\/en(?=\/|$)/, "") || "/";

                        // doelpad
                        const targetPath = shouldBeEn ? `/en${basePath}` : basePath;

                        // ✅ HashRouter redirect: zet hash route en drop search volledig
                        window.location.replace(
                            `${window.location.origin}${window.location.pathname}#${targetPath}${qs}`
                        );
                        return;
                    }
                }

                // taal zetten
                if (globalState.Language !== desired) {
                    await setLanguage(desired);
                    updateGlobalState({ Language: desired, ContentLanguage: desired });
                }

                // global content laden
                const items = await getGlobalContent(
                    { ...globalState, Language: desired },
                    updateGlobalState
                );

                updateGlobalState({
                    Globalcontent: items,
                    Bedrijfsnaam: getGlobalContentitem("BedrijfsnaamLabel", {
                        ...globalState,
                        Globalcontent: items,
                    }).content,
                });

                setLoading(false);
            } catch (err) {
                console.error("Error in App.tsx init:", err);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [effectivePath]);

    // Early return AFTER all hooks
    if (needsAuth && !isAuthenticated) {
        return <div className="content-loading">Redirecting to login...</div>;
    }

    function renderPage() {
        return (
            <CookieConsentProvider globalState={globalState}>
                <Suspense fallback={<div className="content-loading">Loading content...</div>}>
                    <Routes>
                        {/* EN: /en -> /en/ */}
                        <Route path="/en" element={<Navigate to="/en/" replace />} />

                        {/* EN: /en/ -> render dezelfde Home als de index route */}
                        <Route path="/en/" element={<LayoutNoHeader />}>
                            <Route index element={homeElement ?? <div>Home route ontbreekt</div>} />
                        </Route>

                        {/* EN: alle overige geprefixte routes (bijv. /en/cookiebeleid) */}
                        {renderRoutesRecursively(enRoutes as any)}

                        {/* NL: bestaande routes */}
                        {renderRoutesRecursively(AppRoutes as any)}
                    </Routes>
                </Suspense>
            </CookieConsentProvider>
        );
    }

    return (
        <div>
            {loading ? <div className="content-loading">Loading content...</div> : renderPage()}
        </div>
    );
}
